import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from 'react';
import { ApolloError, useApolloClient } from '@apollo/client';
import {
  GetCurrentUserRolesDocument,
  Role,
  UserStatus,
  type GetCurrentUserRolesQuery,
} from '../generated/graphql';
import { useAuth } from '../hooks/useAuth';

interface CurrentUserContextType {
  roles: Role[];
  status: UserStatus | null;
  email: string | null;
  accountId: string | null;
  isAdmin: boolean;
  isNurse: boolean;
  isContent: boolean;
  isDeveloper: boolean;
  isAnalyst: boolean;
  isOperations: boolean;
  loading: boolean;
  /** Set when the roles query failed: roles are then unknown, so the portal fails closed (YOJO-3614). */
  error: ApolloError | undefined;
  /** Asks for the roles again (the "Retry" after a failed roles query). */
  refetchRoles: () => void;
}

const CurrentUserContext = createContext<CurrentUserContextType | null>(null);

/** The roles answer, and the signed-in account it was asked for. */
interface RolesAnswer {
  forUser: string;
  data?: GetCurrentUserRolesQuery;
  error?: ApolloError;
}

/**
 * The signed-in user's roles, asked once per signed-in ACCOUNT (YOJO-3614 [growth staff role +
 * growth MCP door]). Firebase can switch straight from one account to another without a signed-out
 * moment in between (/test-auth signs in over a restored session), so the roles are asked again
 * whenever the account changes, and an answer is only used for the account it was asked for: until
 * the new account's roles arrive the context reports `loading`, never the earlier account's roles.
 */
export function CurrentUserProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, user } = useAuth();
  const client = useApolloClient();
  // The account the roles belong to; `null` while signed out (a user without an id counts as signed out)
  const signedInAs = isAuthenticated && user?.id ? user.id : null;
  const [answer, setAnswer] = useState<RolesAnswer | null>(null);
  // Bumped by Retry: asks again for the same account
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!signedInAs) {
      // A sign-out ends the answer: the next sign-in, even as the same uid, waits for a fresh one
      setAnswer(null);
      return;
    }
    let current = true;
    setAnswer(null);
    client
      .query({ query: GetCurrentUserRolesDocument, fetchPolicy: 'no-cache' })
      .then(({ data }) => {
        if (current) setAnswer({ forUser: signedInAs, data });
      })
      .catch((error: unknown) => {
        if (!current) return;
        setAnswer({
          forUser: signedInAs,
          error: error instanceof ApolloError ? error : new ApolloError({ networkError: error as Error }),
        });
      });
    return () => {
      current = false;
    };
  }, [client, signedInAs, attempt]);

  const refetchRoles = useCallback(() => setAttempt((n) => n + 1), []);

  // An answer for another account (or none yet) is no answer: still loading
  const answered = signedInAs !== null && answer?.forUser === signedInAs ? answer : null;
  const data = answered?.data;
  const error = answered?.error;

  const roles = data?.currentUser?.roles || [];
  const value: CurrentUserContextType = {
    roles,
    status: data?.currentUser?.status || null,
    email: data?.currentUser?.email || null,
    accountId: data?.currentUser?.accountId || null,
    isAdmin: roles.includes(Role.Admin),
    isNurse: roles.includes(Role.Nurse),
    isContent: roles.includes(Role.Content),
    isDeveloper: roles.includes(Role.Developer),
    isAnalyst: roles.includes(Role.Analyst),
    isOperations: roles.includes(Role.Operations),
    loading: !answered,
    error,
    refetchRoles,
  };

  return (
    <CurrentUserContext.Provider value={value}>
      {children}
    </CurrentUserContext.Provider>
  );
}

export function useCurrentUser() {
  const context = useContext(CurrentUserContext);
  if (!context) {
    throw new Error('useCurrentUser must be used within CurrentUserProvider');
  }
  return context;
}
